import { useCallback, useEffect, useState } from "react";
import { request } from "../../api/http";
import { useConfirm } from "../../components/Dialog";
import { Select } from "../../components/FormControls";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import {
  GENDER_OPTIONS,
  PRODUCT_OPTIONS,
  PromoDto,
  PromoListResponse,
  fmtDate,
  formatDefinition,
  formatRange,
  genderLabel,
  productLabel,
} from "../../types/promos";
import CouponLeadsModal from "./components/CouponLeadsModal";
import CreateCouponModal, {
  describeError,
} from "./components/CreateCouponModal";
import EditCouponModal from "./components/EditCouponModal";

const STATUS_OPTIONS = [
  { value: "1", label: "Activos" },
  { value: "0", label: "Inactivos" },
];

export default function Coupons() {
  const { token } = useAuth();
  const { markets } = useMarket();
  const confirm = useConfirm();

  const [items, setItems] = useState<PromoDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState("");
  const [product, setProduct] = useState("");
  const [gender, setGender] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PromoDto | null>(null);
  const [leadsFor, setLeadsFor] = useState<PromoDto | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (product) params.set("product", product);
      if (gender) params.set("gender", gender);
      const qs = params.toString();
      const res = await request<PromoListResponse>(
        `/api/admin/operations/coupons${qs ? `?${qs}` : ""}`,
        { token, markets },
      );
      setItems(res.items);
    } catch (err) {
      setError(describeError(err, "No se pudieron cargar los cupones"));
    } finally {
      setLoading(false);
    }
  }, [token, markets, status, product, gender]);

  useEffect(() => {
    load();
  }, [load]);

  const total = items?.length ?? 0;

  return (
    <div style={{ padding: "1.5rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>Cupones</h1>
        <span style={{ color: "#73879C", fontSize: ".82rem" }}>
          {loading ? "Cargando…" : `${total} cupones`}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setCreateOpen(true)} style={addBtn}>
          + Nuevo cupón
        </button>
      </div>

      <div style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem", maxWidth: 640 }}>
        <FilterCell label="Estado">
          <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} placeholder="Todos" />
        </FilterCell>
        <FilterCell label="Producto">
          <Select value={product} onChange={setProduct} options={PRODUCT_OPTIONS} placeholder="Todos" />
        </FilterCell>
        <FilterCell label="Género">
          <Select value={gender} onChange={setGender} options={GENDER_OPTIONS} placeholder="Todos" />
        </FilterCell>
      </div>

      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".83rem" }}>
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>Código</Th>
              <Th>Nombre</Th>
              <Th>Producto</Th>
              <Th>Definición</Th>
              <Th>Edad</Th>
              <Th>Precio</Th>
              <Th>Género</Th>
              <Th>Vigencia</Th>
              <Th>Usos</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {!loading && items && items.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: "1.5rem", textAlign: "center", color: "#73879C" }}>
                  Sin resultados.
                </td>
              </tr>
            )}
            {items?.map((coupon) => (
              <CouponRow
                key={coupon.promoId}
                coupon={coupon}
                token={token}
                confirm={confirm}
                onReload={load}
                onEdit={() => setEditing(coupon)}
                onLeads={() => setLeadsFor(coupon)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateCouponModal
          token={token}
          onClose={() => setCreateOpen(false)}
          onApplied={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
      {editing && (
        <EditCouponModal
          token={token}
          coupon={editing}
          onClose={() => setEditing(null)}
          onApplied={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {leadsFor && (
        <CouponLeadsModal token={token} coupon={leadsFor} onClose={() => setLeadsFor(null)} />
      )}
    </div>
  );
}

function CouponRow({
  coupon,
  token,
  confirm,
  onReload,
  onEdit,
  onLeads,
}: {
  coupon: PromoDto;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
  onEdit: () => void;
  onLeads: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggle = async () => {
    if (busy) return;
    const ok = await confirm({
      title: coupon.active ? "Desactivar cupón" : "Activar cupón",
      message: `Se ${coupon.active ? "desactivará" : "activará"} el cupón "${
        coupon.couponCode ?? coupon.name
      }". ¿Continuar?`,
      confirmLabel: coupon.active ? "Desactivar" : "Activar",
      tone: coupon.active ? "danger" : "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await request(`/api/admin/operations/coupons/${coupon.promoId}/status`, {
        method: "POST",
        token,
        body: { active: !coupon.active },
      });
      onReload();
    } catch {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      const res = await request<{ link: string }>(
        `/api/admin/operations/coupons/${coupon.promoId}/link`,
        { token },
      );
      await navigator.clipboard.writeText(res.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const uses =
    coupon.couponUsageLimit !== null
      ? `${coupon.couponUses ?? 0} / ${coupon.couponUsageLimit}`
      : `${coupon.couponUses ?? 0}`;

  return (
    <tr style={{ borderTop: "1px solid #f1f3f5" }}>
      <Td><strong>{coupon.couponCode ?? "—"}</strong></Td>
      <Td>{coupon.name}</Td>
      <Td>{productLabel(coupon.product)}</Td>
      <Td>{formatDefinition(coupon)}</Td>
      <Td>{formatRange(coupon.lowerAge, coupon.upperAge)}</Td>
      <Td>{formatRange(coupon.lowerPrice, coupon.upperPrice)}</Td>
      <Td>{genderLabel(coupon.gender)}</Td>
      <Td>{`${fmtDate(coupon.startDate)} → ${fmtDate(coupon.endDate)}`}</Td>
      <Td>{uses}</Td>
      <Td><StatusPill active={coupon.active} /></Td>
      <Td>
        <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
          <ActionButton onClick={toggle} disabled={busy}>
            {coupon.active ? "Desactivar" : "Activar"}
          </ActionButton>
          <ActionButton onClick={onEdit}>Editar</ActionButton>
          <ActionButton onClick={onLeads}>Leads</ActionButton>
          <ActionButton onClick={copyLink}>{copied ? "¡Copiado!" : "Copiar link"}</ActionButton>
        </div>
      </Td>
    </tr>
  );
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.5rem",
        borderRadius: "0.4rem",
        fontSize: ".72rem",
        fontWeight: 600,
        border: "1px solid",
        ...(active
          ? { background: "#d4edda", color: "#155724", borderColor: "#c3e6cb" }
          : { background: "#f1f3f5", color: "#495057", borderColor: "#dee2e6" }),
      }}
    >
      {active ? "Activo" : "Inactivo"}
    </span>
  );
}

function ActionButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.25rem 0.55rem",
        borderRadius: "0.35rem",
        border: "1px solid #dee2e6",
        background: "white",
        color: "#0069d9",
        fontSize: ".74rem",
        fontWeight: 600,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function FilterCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontSize: ".68rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
          marginBottom: "0.25rem",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.6rem 0.7rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".72rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: "0.55rem 0.7rem", verticalAlign: "middle", whiteSpace: "nowrap" }}>
      {children}
    </td>
  );
}

const addBtn: React.CSSProperties = {
  padding: "0.45rem 0.9rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
